import React, { useState, useEffect } from "react";
import { ChevronDown, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { numa as numaApi } from "@/lib/api";
import { useLLM } from "@/contexts/LLMContext";
import NumaLogo from "@/components/NumaLogo";

export default function InlineSuggestionsPanel({ text, onAcceptSuggestion }) {
  const { ready: llmReady } = useLLM();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    // Same rule as the citation hints: silent when no model is connected.
    if (!llmReady || !text || text.trim().length < 20) return;
    
    const timer = setTimeout(() => {
      analyzeText();
    }, 2000); // Delay to avoid excessive API calls while typing

    return () => clearTimeout(timer);
  }, [text, llmReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const analyzeText = async () => {
    setLoading(true);
    try {
      const result = await numaApi.getInlineSuggestions(text);
      setSuggestions(result.suggestions || []);
      if (result.suggestions?.length > 0) {
        setExpanded(true);
      }
    } catch (e) {
      console.error(e);
      // Silent fail - don't show error toast for inline suggestions
    } finally {
      setLoading(false);
    }
  };

  if (suggestions.length === 0 && !loading) return null;

  return (
    <div className="fixed bottom-6 right-6 max-w-sm z-50">
      <Card className="border border-line shadow-lg">
        <div
          className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-surface-2 transition"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center gap-2">
            <NumaLogo size={4} className="rounded" />
            <span className="font-medium text-sm text-ink">
              {suggestions.length} suggeriment{suggestions.length === 1 ? "o" : "i"}
            </span>
            {loading && <div className="w-2 h-2 rounded-full bg-brand animate-pulse" />}
          </div>
          <ChevronDown className={`w-4 h-4 text-ink-muted transition ${expanded ? "rotate-180" : ""}`} />
        </div>

        {expanded && (
          <div className="border-t border-line px-4 py-3 space-y-3 max-h-96 overflow-y-auto">
            {suggestions.map((s, idx) => (
              <div key={idx} className="numa-panel-assistant p-3 space-y-2">
                <div className="text-xs font-medium text-assistant">{s.reason}</div>
                <div className="space-y-1">
                  <div className="text-sm line-through text-[#999] opacity-70">{s.original}</div>
                  <div className="text-sm font-medium text-ok">{s.suggestion}</div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => {
                      setSuggestions(suggestions.filter((_, i) => i !== idx));
                    }}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => {
                      onAcceptSuggestion?.(s);
                      setSuggestions(suggestions.filter((_, i) => i !== idx));
                      toast.success("Suggerimento applicato");
                    }}
                  >
                    <Check className="w-3 h-3 mr-1" /> Applica
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
